import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import Icon from '@amzn/awsui-components-core/icon';
import Multiselect, { MultiselectProps } from '@amzn/awsui-components-core/multiselect';
import Select, { SelectProps } from '@amzn/awsui-components-core/select';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import React, { useCallback, useMemo, useState } from 'react';
import { PnlConfigScenario } from '../starGridTypes';
import styles from './styles/ColumnsPicker.module.scss';

/** Format YYYYMM to "Jan 26" */
export const formatMonth = (monthId: number): string => {
  const year = Math.floor(monthId / 100);
  const month = monthId % 100;
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[month - 1]} ${String(year).slice(-2)}`;
};

/** Group months into fiscal quarters for Multiselect option groups.
 * NOTE: Uses calendar quarters (Math.ceil(month / 3)) which aligns with Amazon's
 * Jan-start fiscal year. If the P&L data ever expands to a non-Jan-start entity,
 * this grouping logic will need a fiscal-year-start offset parameter.
 */
export const groupMonthsByQuarter = (months: readonly number[]): MultiselectProps.Options => {
  const quarterMap = new Map<string, MultiselectProps.Option[]>();

  for (const m of months) {
    const year = Math.floor(m / 100);
    const month = m % 100;
    const quarter = Math.ceil(month / 3);
    const key = `Q${quarter} ${String(year).slice(-2)}`;

    if (!quarterMap.has(key)) {
      quarterMap.set(key, []);
    }
    quarterMap.get(key)!.push({ value: String(m), label: formatMonth(m) });
  }

  return Array.from(quarterMap.entries()).map(([label, options]): MultiselectProps.OptionGroup => ({ label, options }));
};

/**
 * Unique key for a scenario option. Two scenarios can share the same
 * `scenario_name` (e.g. "Guidance Q2-26") but differ by reporting_scenario_id
 * and/or month range, so keying on the label alone collapses them. Combining the
 * id with the full month signature disambiguates every distinct option.
 */
export const scenarioOptionValue = (s: PnlConfigScenario): string => `${s.reporting_scenario_id}__${s.scenario_name}__${s.months.join('-')}`;

/** A single column row: one scenario + selected months for that scenario. */
export interface ColumnEntry {
  readonly id: string;
  readonly scenario: PnlConfigScenario | null;
  readonly selectedMonths: number[];
}

interface ColumnsPickerProps {
  readonly availableScenarios: readonly PnlConfigScenario[];
  readonly columns: readonly ColumnEntry[];
  readonly onChange: (columns: ColumnEntry[]) => void;
  readonly isReadOnly?: boolean;
}

export const generateColumnId = (): string => `col_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/**
 * Columns picker matching the "Add column" pattern (ported from xPT's PnlColumnsPicker):
 * - Each row: drag handle | single-select Scenario | multiselect Period | delete button
 * - "+ Add Column" button at the bottom
 * - Drag-to-reorder rows
 */
const ColumnsPicker: React.FC<ColumnsPickerProps> = ({ availableScenarios, columns, onChange, isReadOnly = false }) => {
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const scenarioSelectOptions = useMemo<SelectProps.Options>(
    () =>
      availableScenarios.map((s) => ({
        value: scenarioOptionValue(s),
        label: s.scenario_name,
        description: `${s.months.length} months (${formatMonth(s.months[0])} – ${formatMonth(s.months[s.months.length - 1])})`
      })),
    [availableScenarios]
  );

  const handleAdd = useCallback((): void => {
    const newEntry: ColumnEntry = {
      id: generateColumnId(),
      scenario: null,
      selectedMonths: []
    };
    onChange([...columns, newEntry]);
  }, [columns, onChange]);

  const handleRemove = useCallback(
    (idx: number): void => {
      onChange(columns.filter((_, i) => i !== idx));
    },
    [columns, onChange]
  );

  const handleScenarioChange = useCallback(
    (idx: number, option: SelectProps.Option): void => {
      const scenario = availableScenarios.find((s) => scenarioOptionValue(s) === option.value);
      if (!scenario) return;

      const updated = columns.map((col, i) => {
        if (i !== idx) return col;
        // Start with no months selected — user picks periods explicitly
        return { ...col, scenario, selectedMonths: [] };
      });
      onChange(updated);
    },
    [availableScenarios, columns, onChange]
  );

  const handleMonthsChange = useCallback(
    (idx: number, selectedOptions: readonly MultiselectProps.Option[]): void => {
      const months = selectedOptions
        .map((o) => Number(o.value))
        .filter((n) => !Number.isNaN(n))
        .sort((a, b) => a - b);
      const updated = columns.map((col, i) => {
        if (i !== idx) return col;
        return { ...col, selectedMonths: months };
      });
      onChange(updated);
    },
    [columns, onChange]
  );

  // Drag-and-drop handlers
  const handleDragStart = useCallback((e: React.DragEvent<HTMLDivElement>, idx: number): void => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>, idx: number): void => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dragIdx !== null && idx !== dragIdx) {
        setDragOverIdx(idx);
      }
    },
    [dragIdx]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>, dropIdx: number): void => {
      e.preventDefault();
      if (dragIdx === null || dragIdx === dropIdx) {
        setDragIdx(null);
        setDragOverIdx(null);
        return;
      }
      const reordered = [...columns];
      const [moved] = reordered.splice(dragIdx, 1);
      reordered.splice(dropIdx, 0, moved);
      onChange(reordered);
      setDragIdx(null);
      setDragOverIdx(null);
    },
    [dragIdx, columns, onChange]
  );

  const handleDragEnd = useCallback((): void => {
    setDragIdx(null);
    setDragOverIdx(null);
  }, []);

  return (
    <SpaceBetween size="s">
      {/* Column headers */}
      {columns.length > 0 && (
        <div className={styles.columnHeader}>
          <Box variant="span" />
          <Box variant="awsui-key-label">Scenario</Box>
          <Box variant="awsui-key-label">Period</Box>
          <Box variant="span" />
        </div>
      )}

      {/* Column rows */}
      {columns.map((col, idx) => {
        // Always derive month options from the live availableScenarios list
        // so they stay in sync if the config changes. Match on the unique key so
        // same-named scenarios don't collapse onto the first match.
        const liveScenario = col.scenario ? availableScenarios.find((s) => scenarioOptionValue(s) === scenarioOptionValue(col.scenario!)) : null;
        const monthOptions: MultiselectProps.Options = liveScenario ? groupMonthsByQuarter(liveScenario.months) : [];

        const selectedMonthOptions: MultiselectProps.Option[] = col.selectedMonths.map((m) => ({
          value: String(m),
          label: formatMonth(m)
        }));

        const selectedScenarioOption: SelectProps.Option | null = col.scenario
          ? { value: scenarioOptionValue(col.scenario), label: col.scenario.scenario_name }
          : null;

        const isDragOver = dragOverIdx === idx && dragIdx !== idx;

        return (
          <div
            key={col.id}
            draggable={!isReadOnly}
            onDragStart={(e) => handleDragStart(e, idx)}
            onDragOver={(e) => handleDragOver(e, idx)}
            onDrop={(e) => handleDrop(e, idx)}
            onDragEnd={handleDragEnd}
            className={[
              styles.columnRow,
              isReadOnly ? styles.columnRowReadOnly : '',
              isDragOver ? styles.columnRowDragOver : '',
              dragIdx === idx ? styles.columnRowDragging : ''
            ]
              .filter(Boolean)
              .join(' ')}
            data-testid="stargrid-columns-row"
            data-index={idx}
            data-scenario={col.scenario?.scenario_name ?? ''}
          >
            {/* Drag handle */}
            <Box textAlign="center">{!isReadOnly && <Icon name="drag-indicator" variant="subtle" />}</Box>

            {/* Scenario single-select */}
            <Select
              selectedOption={selectedScenarioOption}
              options={scenarioSelectOptions}
              onChange={({ detail }) => handleScenarioChange(idx, detail.selectedOption)}
              placeholder="Select Scenario"
              disabled={isReadOnly}
              filteringType="auto"
              expandToViewport
              data-testid="stargrid-columns-scenario-select"
            />

            {/* Period multiselect */}
            <Multiselect
              selectedOptions={selectedMonthOptions}
              options={monthOptions}
              onChange={({ detail }) => handleMonthsChange(idx, detail.selectedOptions)}
              placeholder="Select Month"
              disabled={isReadOnly || !col.scenario}
              enableSelectAll
              i18nStrings={{ selectAllText: 'Select all' }}
              filteringType="auto"
              expandToViewport
              data-testid="stargrid-columns-months-multiselect"
            />

            {/* Delete button */}
            {!isReadOnly && (
              <Button
                variant="icon"
                iconName="remove"
                ariaLabel={`Remove column ${idx + 1}`}
                onClick={() => handleRemove(idx)}
                data-testid="stargrid-columns-remove"
              />
            )}
          </div>
        );
      })}

      {/* Add column button */}
      {!isReadOnly && (
        <Box>
          <Button iconName="add-plus" onClick={handleAdd} data-testid="stargrid-columns-add">
            Add Column
          </Button>
        </Box>
      )}
    </SpaceBetween>
  );
};

export default ColumnsPicker;
